import { useEffect } from 'react';

/**
 * useBodyScrollLock — locks page scroll while `locked` is true.
 *
 * Pins the body with position:fixed at the current scroll offset instead of
 * relying on overflow:hidden, because iOS Safari ignores overflow:hidden for
 * touch scrolling. Restores the exact scroll position on unlock.
 *
 * The restore is forced to an instant jump: globals.css sets
 * `scroll-behavior: smooth`, and pinning the body collapses the scroll offset
 * to 0, so a default-behavior scrollTo animates a visible whoosh from the top
 * back down to where the user actually was.
 *
 * @param {boolean} locked
 */
const useBodyScrollLock = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const prev = {
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      width: style.width,
      overflow: style.overflow,
    };

    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.left = '0';
    style.right = '0';
    style.width = '100%';
    style.overflow = 'hidden';

    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.left = prev.left;
      style.right = prev.right;
      style.width = prev.width;
      style.overflow = prev.overflow;
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    };
  }, [locked]);
};

export default useBodyScrollLock;
